import { useState, useEffect, useCallback, useRef } from 'react';
import callService from '../services/CallService';

const POLLING_MS = 3000;

export default function useIncomingCall(serviceId, isActive = true) {
  const [incomingCall, setIncomingCall] = useState(null);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);
  const incomingCallRef = useRef(null);

  useEffect(() => {
    incomingCallRef.current = incomingCall;
  }, [incomingCall]);

  const stopRingtone = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, []);

  const playRingtone = useCallback(() => {
    stopRingtone();
    try {
      const audio = new Audio('/sounds/incoming-call.mp3');
      audio.loop = true;
      audio.play().catch(() => {});
      audioRef.current = audio;
    } catch (error) {
      console.error('Ringtone error:', error);
    }
  }, [stopRingtone]);

  const clearIncomingCall = useCallback(() => {
    stopRingtone();
    setIncomingCall(null);
  }, [stopRingtone]);

  const fetchIncomingCalls = useCallback(async () => {
    if (!serviceId || !isActive) return;

    try {
      setLoading(true);
      const calls = await callService.getIncomingCalls(serviceId, 5);
      const list = Array.isArray(calls) ? calls : [];

      if (list.length === 0) {
        if (incomingCallRef.current) {
          const currentId = incomingCallRef.current.call_id;
          const stillRinging = list.some((call) => call.call_id === currentId);
          if (!stillRinging) {
            clearIncomingCall();
          }
        }
        return;
      }

      const nextCall = list[0];
      const current = incomingCallRef.current;

      if (!current || current.call_id !== nextCall.call_id) {
        setIncomingCall(nextCall);
        playRingtone();
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [serviceId, isActive, clearIncomingCall, playRingtone]);

  useEffect(() => {
    fetchIncomingCalls();
  }, [fetchIncomingCalls]);

  useEffect(() => {
    if (!serviceId || !isActive) return undefined;
    const id = setInterval(fetchIncomingCalls, POLLING_MS);
    return () => clearInterval(id);
  }, [serviceId, isActive, fetchIncomingCalls]);

  useEffect(() => () => stopRingtone(), [stopRingtone]);

  return { incomingCall, loading, clearIncomingCall, stopRingtone, refreshIncomingCalls: fetchIncomingCalls };
}
